import { useRef, useState } from 'react';
import { MinusIcon, PencilSquareIcon, PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { Cart } from 'shared/types/cart';
import { ExtendedLineItem } from 'shared/types/cart/LineItem';
import { Money } from 'shared/types/product';
import { BuilderDesignGroup, builderDesignGroupsOf } from 'components/commercetools-ui/organisms/cart/components/builderDesignGroups';
import { uniformUnitPrice } from 'components/commercetools-ui/organisms/cart/components/item-price';
import { brandAsset } from 'helpers/brandAssets';
import { getCartItemLinks } from 'helpers/cart/cartItemLinks';
import { CurrencyHelpers } from 'helpers/currencyHelpers';
import useI18n from 'helpers/hooks/useI18n';
import { RemovedDesignLines } from 'frontastic/hooks/useCart/types';
import BrandImage from './brand-image';

interface CartDrawerProps {
  cart?: Cart;
  isEmpty?: boolean;
  onUpdateItem: (
    lineItemId: string,
    customLineItemId: string | undefined,
    quantity: number,
  ) => Promise<{ isError: boolean; data?: Cart; error?: unknown }>;
  onRemoveItem: (lineItemId: string, customLineItemId?: string) => Promise<void>;
  onRemoveDesign: (designId: string) => Promise<RemovedDesignLines>;
  onApplyDiscountCode?: (code: string) => Promise<void>;
  onClose: () => void;
}

// The server refuses some cart writes with a 409 (e.g. a finish it cannot attribute to the row) and
// says why; that message is what the shopper needs, so it is shown in preference to the fallback.
const errorMessageOf = (error: unknown, fallback: string) => (error as Error)?.message || fallback;

// Split display rows share one lineItemId, so the key needs the finish's customLineItemId too.
const rowKeyOf = (item: ExtendedLineItem) => `${item.lineItemId}-${item.customLineItemId ?? 'item'}`;

interface DrawerLineProps {
  item: ExtendedLineItem;
  /** The row's design is being removed as a whole: no row write may overlap that one. */
  locked?: boolean;
  formatPrice: (money?: Money) => string;
  onUpdate: (quantity: number) => Promise<void>;
  onRemove: () => Promise<void>;
  onClose: () => void;
}

const DrawerLine = ({ item, locked = false, formatPrice, onUpdate, onRemove, onClose }: DrawerLineProps) => {
  const [processing, setProcessing] = useState(false);
  const count = item.count ?? 1;
  const { editUrl, addVariantUrl } = getCartItemLinks(item);
  const disabled = processing || locked;

  const run = async (write: () => Promise<void>) => {
    if (disabled) return;
    setProcessing(true);
    try {
      await write();
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div data-testid="drawer-line" className="border-b border-[#d7d7d7] px-24 py-20">
      <div className="flex gap-16">
        <BrandImage
          src={item.custom?.fields?.configThumbnailUrl ?? item.variant?.images?.[0] ?? ''}
          alt={item.name ?? ''}
          width={200}
          className="size-99 shrink-0 bg-[#e8e8e8] object-cover"
        />
        <div className="flex min-w-0 flex-1 flex-col gap-12">
          <p className="truncate text-16 leading-[1.2]">{item.name}</p>
          <div className="flex w-fit items-center border border-[#d7d7d7]">
            <button
              aria-label="Decrease quantity"
              disabled={disabled}
              onClick={() => run(() => onUpdate(Math.max(1, count - 1)))}
              className="flex size-40 items-center justify-center transition-colors hover:bg-[#f0f0f0] disabled:opacity-40"
            >
              <MinusIcon className="size-14" />
            </button>
            <span className="w-40 text-center text-16">{count}</span>
            <button
              aria-label="Increase quantity"
              disabled={disabled}
              onClick={() => run(() => onUpdate(count + 1))}
              className="flex size-40 items-center justify-center transition-colors hover:bg-[#f0f0f0] disabled:opacity-40"
            >
              <PlusIcon className="size-14" />
            </button>
          </div>
          <div className="text-right">
            <p className="text-16 font-medium">{formatPrice(item.totalPrice ?? item.price)}</p>
            {/* Omitted when units carry different discounts: no single "each" figure matches the total. */}
            {count > 1 && uniformUnitPrice(item) && (
              <p className="text-12 text-neutral-black-400">{formatPrice(uniformUnitPrice(item))} each</p>
            )}
          </div>
        </div>
      </div>

      <div className="mt-8 flex items-center gap-24 text-14">
        {editUrl && (
          <a href={editUrl} onClick={onClose} className="flex min-h-36 items-center gap-6 transition-opacity hover:opacity-70">
            <PencilSquareIcon className="size-16" />
            Edit
          </a>
        )}
        {addVariantUrl && (
          <a href={addVariantUrl} onClick={onClose} className="flex min-h-36 items-center transition-opacity hover:opacity-70">
            Add Variant
          </a>
        )}
        <button
          disabled={disabled}
          onClick={() => run(onRemove)}
          className="flex min-h-36 items-center font-gt-s font-bold transition-opacity hover:opacity-70 disabled:cursor-not-allowed disabled:opacity-40"
        >
          Remove
        </button>
      </div>
    </div>
  );
};

interface DrawerDesignGroupProps extends Omit<DrawerLineProps, 'item' | 'locked' | 'onUpdate' | 'onRemove'> {
  group: BuilderDesignGroup;
  onRemoveDesign: () => Promise<void>;
  onUpdateLine: (item: ExtendedLineItem, quantity: number) => Promise<void>;
  onRemoveLine: (item: ExtendedLineItem) => Promise<void>;
}

/* Mirrors the cart page's builder design group (DesignSystemAccordion): "Remove design" is ONE server
   write, so it waits for this group's row writes to settle and locks the rows while it runs. */
const DrawerDesignGroup = ({ group, onRemoveDesign, onUpdateLine, onRemoveLine, ...lineProps }: DrawerDesignGroupProps) => {
  const [removing, setRemoving] = useState(false);
  const [rowsInFlight, setRowsInFlight] = useState(0);
  // Refs are read at click time, where state from the last render could be stale.
  const removingRef = useRef(false);
  const rowsInFlightRef = useRef(0);

  const trackRow = async (write: () => Promise<void>) => {
    if (removingRef.current) return;
    rowsInFlightRef.current += 1;
    setRowsInFlight(rowsInFlightRef.current);
    try {
      await write();
    } finally {
      rowsInFlightRef.current -= 1;
      setRowsInFlight(rowsInFlightRef.current);
    }
  };

  const handleRemoveDesign = async () => {
    if (removingRef.current || rowsInFlightRef.current > 0) return;
    removingRef.current = true;
    setRemoving(true);
    try {
      await onRemoveDesign();
    } finally {
      removingRef.current = false;
      setRemoving(false);
    }
  };

  return (
    <div data-testid="drawer-design-group" className="border-b border-[#d7d7d7]">
      <div className="flex items-center justify-between gap-16 px-24 pt-20">
        <p className="min-w-0 truncate text-16 font-medium leading-[1.2]" title={group.title}>
          {group.title}
        </p>
        <div className="flex shrink-0 items-center gap-16 text-14">
          {group.editUrl && (
            <a href={group.editUrl} onClick={lineProps.onClose} className="flex min-h-36 items-center transition-opacity hover:opacity-70">
              Edit design
            </a>
          )}
          <button
            disabled={removing || rowsInFlight > 0}
            onClick={handleRemoveDesign}
            className="flex min-h-36 items-center font-gt-s font-bold transition-opacity hover:opacity-70 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {removing ? 'Removing…' : 'Remove design'}
          </button>
        </div>
      </div>
      {group.items.map((item) => (
        <DrawerLine
          key={rowKeyOf(item)}
          item={item}
          locked={removing}
          onUpdate={(quantity) => trackRow(() => onUpdateLine(item, quantity))}
          onRemove={() => trackRow(() => onRemoveLine(item))}
          {...lineProps}
        />
      ))}
    </div>
  );
};

const CartDrawer = ({ cart, isEmpty, onUpdateItem, onRemoveItem, onRemoveDesign, onApplyDiscountCode, onClose }: CartDrawerProps) => {
  const { locale, currency } = useI18n();
  const [discountOpen, setDiscountOpen] = useState(false);
  const [discountCode, setDiscountCode] = useState('');

  const lineItems = cart?.lineItems ?? [];
  const { groups: designGroups, flat: flatItems } = builderDesignGroupsOf(lineItems);
  const formatPrice = (money?: Money) => (money ? CurrencyHelpers.formatForCurrency(money, locale, currency) : '');

  // updateItem reports a refusal in its result; removeItem, removeDesign and redeemDiscountCode throw.
  const updateLine = async (item: ExtendedLineItem, quantity: number) => {
    const result = await onUpdateItem(item.lineItemId ?? '', item.customLineItemId, quantity);
    if (result.isError) toast.error(errorMessageOf(result.error, 'We could not update that item. Your cart was not changed.'));
  };

  const removeLine = async (item: ExtendedLineItem) => {
    try {
      await onRemoveItem(item.lineItemId ?? '', item.customLineItemId);
    } catch (error) {
      toast.error(errorMessageOf(error, 'We could not remove that item. Your cart was not changed.'));
    }
  };

  const removeDesign = async (designId: string) => {
    try {
      await onRemoveDesign(designId);
    } catch (error) {
      toast.error(errorMessageOf(error, 'We could not remove that design. Your cart was not changed.'));
    }
  };

  const applyDiscountCode = async (code: string) => {
    if (!onApplyDiscountCode) return;
    try {
      await onApplyDiscountCode(code);
    } catch (error) {
      toast.error(errorMessageOf(error, 'That discount code could not be applied.'));
    }
  };

  return (
    <div className="flex h-full flex-col bg-white font-gt text-[#212121]">
      {/* Header */}
      <div className="flex items-center justify-between px-24 py-20">
        <h2 className="font-gt text-24 font-medium leading-[1.2]">My Cart</h2>
        <button aria-label="Close cart" onClick={onClose} className="flex size-44 items-center justify-center transition-opacity hover:opacity-70">
          <XMarkIcon className="size-24" />
        </button>
      </div>
      <div className="h-px bg-[#d7d7d7]" />

      {/* Line items: builder designs grouped the same way the cart page groups them, then flat rows. */}
      <div className="flex-1 overflow-y-auto">
        {isEmpty || lineItems.length === 0 ? (
          <p className="px-24 py-40 text-16 leading-[1.5] text-[#5e5e5e]">Your cart is empty.</p>
        ) : (
          <>
            {designGroups.map((group) => (
              <DrawerDesignGroup
                key={group.designId}
                group={group}
                formatPrice={formatPrice}
                onRemoveDesign={() => removeDesign(group.designId)}
                onUpdateLine={updateLine}
                onRemoveLine={removeLine}
                onClose={onClose}
              />
            ))}
            {flatItems.map((item) => (
              <DrawerLine
                key={rowKeyOf(item)}
                item={item}
                formatPrice={formatPrice}
                onUpdate={(quantity) => updateLine(item, quantity)}
                onRemove={() => removeLine(item)}
                onClose={onClose}
              />
            ))}
          </>
        )}
      </div>

      {/* Footer — full-width dividers bracket the Go to Cart / discount / payment rows. */}
      <div className="shrink-0">
        <div className="h-px bg-[#d7d7d7]" />
        <div className="px-24 py-16">
          <a
            href="/cart/"
            className="flex h-44 items-center justify-center bg-[#1c1c1c] px-32 text-14 text-white transition-opacity hover:opacity-90"
          >
            Go to Cart
          </a>
        </div>

        <div className="h-px bg-[#d7d7d7]" />
        <div className="px-24 py-16">
          <button
            onClick={() => setDiscountOpen((open) => !open)}
            className="flex min-h-44 w-full items-center justify-between text-16"
          >
            Apply a Discount Code
            <PlusIcon className={`size-16 transition-transform ${discountOpen ? 'rotate-45' : ''}`} />
          </button>
          {onApplyDiscountCode && (
            <div
              className={`overflow-hidden transition-[max-height,opacity] duration-500 ease-out ${
                discountOpen ? 'max-h-120 opacity-100' : 'max-h-0 opacity-0'
              }`}
            >
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  if (discountCode.trim()) applyDiscountCode(discountCode.trim());
                }}
                className="mt-12 flex gap-8"
              >
                <input
                  value={discountCode}
                  onChange={(e) => setDiscountCode(e.target.value)}
                  placeholder="Code"
                  className="h-44 min-w-0 flex-1 border border-[#d7d7d7] px-12 text-14 outline-none"
                />
                <button type="submit" className="h-44 shrink-0 bg-[#1c1c1c] px-24 text-14 text-white transition-opacity hover:opacity-90">
                  Apply
                </button>
              </form>
            </div>
          )}
        </div>

        <div className="h-px bg-[#d7d7d7]" />
        <div className="px-24 py-16">
          <BrandImage
            src={brandAsset('cart-payment-logos.png')}
            alt="Accepted payment methods"
            className="h-auto w-full max-w-300"
          />
        </div>
      </div>
    </div>
  );
};

export default CartDrawer;
